import { createApi } from '@reduxjs/toolkit/query/react';
import type { Dispatch } from '@reduxjs/toolkit';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';
import { assetApiSlice } from '../asset/assetApiSlice';
import { bookingApiSlice } from '../booking/bookingApiSlice';
import { groupAdminApiSlice } from '../group/groupAdminApiSlice';
import { locationsApiSlice } from '../locations/locationsApiSlice';
import { productObjectsApiSlice } from '../product/productObjectsApiSlice';
import type {
    StationProductDefaultsResponse,
    StationProductDefaultsRow,
    UpdateStationProductDefaultsRequest,
} from '../station/stationDefaultsApiTypes';
import type {
    CombineRoomsRequest,
    CombineRoomsResponse,
    ConsolidateRoomResponse,
    CreateObjectsRequest,
    CreateObjectsResponse,
    CreateStationRequest,
    CreateStationResponse,
    DivideRoomRequest,
    DivideRoomResponse,
    HouseListItem,
    LinkObjectsToProductRequest,
    LinkObjectsToProductResponse,
    ObjectLimitResponse,
    ReactivateObjectResponse,
    RelinkObjectRequest,
    RelinkObjectResponse,
    RetireObjectResponse,
    RetiredObjectListItem,
    StationDetailResponse,
    UpdateObjectRequest,
    UpdateObjectResponse,
    UpdateStationLocationRequest,
    UpdateStationRequest,
} from '../station/stationApiTypes';

// Target-model inventory writes — `/admin/inventory` on the new (ECS) backend
// (#2134, merged in #2225 and QA'd in #2226).
//
// Every request and response type comes from `api/station/stationApiTypes.ts`,
// which is the authoritative contract: it was written before the endpoints and
// the backend has a regression test that reads it and asserts the controller
// matches. Nothing is re-declared here — a second declaration is how the two
// drift.

export const inventoryApiSlice = createApi({
    reducerPath: 'inventoryApiSlice',
    // The tenant travels in a header, so without this every tenant shares one
    // cache entry and a subscribed query keeps serving the previous tenant's
    // response (#1848 → #1855).
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    // One tag, for the one thing this slice now READS. Everything else that
    // shows inventory still lives in other slices, which tags cannot reach —
    // that eviction is `invalidateInventoryReaders` below, called explicitly.
    // The tag is not a substitute for it: it only refreshes the station page
    // itself after its own edit.
    tagTypes: ['Station', 'Houses', 'RetiredObjects', 'ObjectLimit', 'StationProductDefaults'],
    endpoints: (builder) => ({
        createStation: builder.mutation<CreateStationResponse, CreateStationRequest>({
            query: (body) => ({
                url: '/inventory/stations',
                method: 'POST',
                data: body,
            }),
            // A new house must appear in the room form's house picker.
            invalidatesTags: ['Houses', 'ObjectLimit'],
        }),

        /**
         * "Dem Haus zuordnen" (plan room-houses-and-combining-rooms §4 row 3).
         * Resolves to `POST <newBackendUrl>/admin/inventory/objects/<id>/consolidate`.
         * The house list's room counts change, so `Houses` is invalidated; the
         * room page itself is cleared by `invalidateInventoryReaders`.
         */
        consolidateRoom: builder.mutation<ConsolidateRoomResponse, { assetId: string, houseId: string }>({
            query: ({ assetId, houseId }) => ({
                url: `/inventory/objects/${encodeURIComponent(assetId)}/consolidate`,
                method: 'POST',
                data: { houseId },
            }),
            // 'Station' too: the house's room list gains the room. The request
            // names the house, but the room's old automatic station changes as
            // well, so the whole tag rather than one id.
            invalidatesTags: ['Station', 'Houses'],
        }),

        /** The tenant's houses — the room form's "in an existing house" picker. */
        getHouses: builder.query<HouseListItem[], void>({
            query: () => ({ url: '/inventory/houses', method: 'GET' }),
            providesTags: ['Houses'],
        }),

        // Creates the objects AND their product links in one transaction when
        // `productId` is present — see the contract's note on why the wizard
        // uses this rather than a separate link call.
        //
        // Invalidates the station it wrote to. The wizard never needed this — it
        // navigates away — but "add objects to an existing station" (#2280)
        // stays on the page it just changed, and without the tag the new
        // compartments are absent from the position list until a reload, which
        // reads as "the add silently failed".
        createStationObjects: builder.mutation<CreateObjectsResponse, CreateObjectsRequest>({
            query: ({ stationId, ...body }) => ({
                url: `/inventory/stations/${encodeURIComponent(stationId)}/objects`,
                method: 'POST',
                data: body,
            }),
            invalidatesTags: (result, error, { stationId }) => [
                { type: 'Station' as const, id: stationId },
                // A renamed house is renamed in the house picker too.
                'Houses',
                'ObjectLimit',
            ],
        }),

        /** Link objects that already exist. The wizard does not use this. */
        linkObjectsToProduct: builder.mutation<LinkObjectsToProductResponse, LinkObjectsToProductRequest>({
            query: (body) => ({
                url: '/inventory/product-links',
                method: 'POST',
                data: body,
            }),
        }),

        relinkObject: builder.mutation<RelinkObjectResponse, RelinkObjectRequest>({
            query: ({ linkId, ...body }) => ({
                url: `/inventory/product-links/${encodeURIComponent(linkId)}`,
                method: 'PATCH',
                data: body,
            }),
            // The whole tag, not one station: the request is addressed by link
            // id and carries no station id, so which station changed is not
            // knowable here. Station reads are one page at a time, so the cost
            // of refetching them all is one request — and without this the
            // position list keeps showing the product the object no longer has.
            invalidatesTags: ['Station'],
        }),

        updateObject: builder.mutation<UpdateObjectResponse, UpdateObjectRequest>({
            query: ({ assetId, ...body }) => ({
                url: `/inventory/objects/${encodeURIComponent(assetId)}`,
                method: 'PATCH',
                data: body,
            }),
        }),

        /**
         * The station detail page's read. Tagged per station id so an edit
         * refreshes the page it was made on and nothing else.
         */
        getStation: builder.query<StationDetailResponse, { stationId: string }>({
            query: ({ stationId }) => ({
                url: `/inventory/stations/${encodeURIComponent(stationId)}`,
                method: 'GET',
            }),
            providesTags: (result, error, { stationId }) => [
                { type: 'Station' as const, id: stationId },
            ],
        }),

        /**
         * D15 (product refactor 4.4): the station's product rows with their
         * own per-booking limit and checklist, the product's default and the
         * effective value. The product default is edited in another slice
         * (`updateProduct`), which tags cannot reach: the reader refetches on
         * mount (`StationProductDefaultsSection`).
         */
        getStationProductDefaults: builder.query<StationProductDefaultsResponse, { stationId: string }>({
            query: ({ stationId }) => ({
                url: `/inventory/stations/${encodeURIComponent(stationId)}/product-defaults`,
                method: 'GET',
            }),
            providesTags: (result, error, { stationId }) => [
                { type: 'StationProductDefaults' as const, id: stationId },
            ],
        }),

        updateStationProductDefaults: builder.mutation<StationProductDefaultsRow, UpdateStationProductDefaultsRequest>({
            query: ({ stationId, groupId, ...body }) => ({
                url: `/inventory/stations/${encodeURIComponent(stationId)}/product-defaults/${encodeURIComponent(groupId)}`,
                method: 'PATCH',
                data: body,
            }),
            // The whole tag, not this station's: a row is per location, so
            // another station there shows the same override.
            invalidatesTags: ['StationProductDefaults'],
        }),

        updateStation: builder.mutation<
            StationDetailResponse, UpdateStationRequest & { stationId: string }
        >({
            query: ({ stationId, ...body }) => ({
                url: `/inventory/stations/${encodeURIComponent(stationId)}`,
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: (result, error, { stationId }) => [
                { type: 'Station' as const, id: stationId },
            ],
        }),

        /**
         * The SHARED address and opening hours, addressed through the station.
         *
         * Invalidates only this station's tag even though the write reaches
         * every station at the location: the others are not on screen, and
         * `invalidateInventoryReaders` clears the lists that show them.
         * 'Houses' too: the house picker shows each house's opening hours.
         */
        // The location's photo (ADR-0003 amendment 2026-10-19): replaces the
        // shown one; DELETE drops it. Same invalidation as the location edit.
        uploadStationLocationImage: builder.mutation<StationDetailResponse, { stationId: string, file: File }>({
            query: ({ stationId, file }) => {
                const formData = new FormData();
                formData.append('image', file);
                return {
                    url: `/inventory/stations/${encodeURIComponent(stationId)}/location/image`,
                    method: 'PUT',
                    data: formData,
                    headers: { 'Content-Type': 'multipart/form-data' },
                };
            },
            invalidatesTags: (result, error, { stationId }) => [{ type: 'Station' as const, id: stationId }],
        }),
        // Delete an EMPTY house for good — master only, refused with reasons
        // otherwise (#2636 pattern). The overview and house pickers refetch
        // through `invalidateInventoryReaders`.
        deleteHouse: builder.mutation<{ id: string, deletedLocation: boolean }, { stationId: string }>({
            query: ({ stationId }) => ({
                url: `/inventory/stations/${encodeURIComponent(stationId)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Houses'],
        }),
        removeStationLocationImage: builder.mutation<StationDetailResponse, { stationId: string }>({
            query: ({ stationId }) => ({
                url: `/inventory/stations/${encodeURIComponent(stationId)}/location/image`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, { stationId }) => [{ type: 'Station' as const, id: stationId }],
        }),
        updateStationLocation: builder.mutation<
            StationDetailResponse, UpdateStationLocationRequest & { stationId: string }
        >({
            query: ({ stationId, ...body }) => ({
                url: `/inventory/stations/${encodeURIComponent(stationId)}/location`,
                method: 'PATCH',
                data: body,
            }),
            invalidatesTags: (result, error, { stationId }) => [
                { type: 'Station' as const, id: stationId },
                'Houses',
            ],
        }),

        retireObject: builder.mutation<RetireObjectResponse, { assetId: string }>({
            query: ({ assetId }) => ({
                url: `/inventory/objects/${encodeURIComponent(assetId)}/retire`,
                method: 'POST',
            }),
            // The whole tag, for the same reason as `relinkObject`: the request
            // is addressed by asset id and carries no station id, so which
            // station changed is not knowable here.
            //
            // It had none at all, which was invisible while retire lived only on
            // the PRODUCT page — that page's fleet comes from
            // `productObjectsApiSlice`, which `invalidateInventoryReaders`
            // clears. Retiring from the station's position list reads
            // `getStation` in THIS slice, and without this the compartment stays
            // in the list after it is archived: a successful retire that looks
            // like it did nothing.
            invalidatesTags: ['Station', 'Houses', 'RetiredObjects', 'ObjectLimit'],
        }),

        /** "Wieder in Betrieb nehmen": undoes exactly what the retire archived. */
        reactivateObject: builder.mutation<ReactivateObjectResponse, { assetId: string }>({
            query: ({ assetId }) => ({
                url: `/inventory/objects/${encodeURIComponent(assetId)}/reactivate`,
                method: 'POST',
            }),
            invalidatesTags: ['Station', 'Houses', 'RetiredObjects', 'ObjectLimit'],
        }),

        /**
         * The trial object limit as the API enforces it (#2202) — the banner's
         * numbers. Resolves to `GET <newBackendUrl>/admin/inventory/object-limit`.
         * Refreshed by this slice's own creates and by `invalidateInventoryReaders`
         * for creates in other slices (room create).
         */
        getObjectLimit: builder.query<ObjectLimitResponse, void>({
            query: () => ({ url: '/inventory/object-limit', method: 'GET' }),
            providesTags: ['ObjectLimit'],
        }),

        /** The objects "Ausgemusterte anzeigen" lists in the Objekte overview. */
        getRetiredObjects: builder.query<RetiredObjectListItem[], void>({
            query: () => ({ url: '/inventory/objects/retired', method: 'GET' }),
            transformResponse: (response: { objects: RetiredObjectListItem[] }) => response.objects,
            providesTags: ['RetiredObjects'],
        }),

        /**
         * Divide a single-object room into 2-3 bookable segments (#1714).
         *
         * `/inventory/...` without a leading `/admin`: the new-backend base
         * query already carries it, and writing it twice is the 404 that hit
         * five slices across #2261 and #2266. The resolved request is
         * `POST <newBackendUrl>/admin/inventory/products/<id>/divide`, which is
         * what `inventoryRoutes.ts` registers.
         *
         * No `invalidatesTags` of its own — what this write changes lives in
         * four other slices, so the eviction is `invalidateInventoryReaders`,
         * called by the dialog. A tag here would refresh the station page,
         * which is not where the new segments appear.
         */
        divideRoom: builder.mutation<DivideRoomResponse, DivideRoomRequest>({
            query: ({ productId, segments, wholeRoomCapacity }) => ({
                url: `/inventory/products/${encodeURIComponent(productId)}/divide`,
                method: 'POST',
                data: wholeRoomCapacity === undefined ? { segments } : { segments, wholeRoomCapacity },
            }),
        }),

        /**
         * Combine existing rooms into a whole room (plan
         * room-houses-and-combining-rooms §4 row 4). Resolves to
         * `POST <newBackendUrl>/admin/inventory/products/<id>/combine`. No tags
         * of its own, for the reason given on `divideRoom`.
         */
        combineRooms: builder.mutation<CombineRoomsResponse, CombineRoomsRequest>({
            query: ({ productId, partProductIds, wholeRoomCapacity }) => ({
                url: `/inventory/products/${encodeURIComponent(productId)}/combine`,
                method: 'POST',
                data: wholeRoomCapacity === undefined
                    ? { partProductIds }
                    : { partProductIds, wholeRoomCapacity },
            }),
        }),
    }),
});

/**
 * Evict everything that reads inventory, after a write changed it.
 *
 * `invalidatesTags` cannot cross a `createApi` boundary, and the surfaces that
 * show stations and objects are spread over five slices — so the eviction has
 * to be explicit. It lives here, once, rather than at each call site: a write
 * that forgot one of these leaves a retired object still listed, which reads as
 * "the retire silently failed".
 */
export const invalidateInventoryReaders = (dispatch: Dispatch) => {
    dispatch(productObjectsApiSlice.util.invalidateTags(['ProductFleet']));
    dispatch(assetApiSlice.util.invalidateTags(['assetDetails']));
    dispatch(bookingApiSlice.util.invalidateTags(['assetAvailabilityData']));
    // 'Products' and 'PricingStructure' joined the list for #1714: dividing a
    // room MINTS Products, Groups and price categories, so the Services lists
    // that name them are stale the moment it returns. Without them the segments
    // an operator just created are invisible until a reload — a successful
    // write that looks like it did nothing, which is exactly the shape the
    // retire bug had.
    dispatch(groupAdminApiSlice.util.invalidateTags([
        'AdminGroups', 'Products', 'PricingStructure',
    ]));
    dispatch(locationsApiSlice.util.invalidateTags(['locationList', 'Groups']));
    // This slice's own readers too: a house page lists its rooms and the house
    // picker counts them, and both change with writes made from other slices
    // (room create, room product change).
    // 'ObjectLimit': the trial banner's count (#2202) moves with every create,
    // retire and reactivate, whichever slice made it.
    // 'StationProductDefaults': a station's product rows follow its objects.
    dispatch(inventoryApiSlice.util.invalidateTags([
        'Station', 'Houses', 'RetiredObjects', 'ObjectLimit', 'StationProductDefaults',
    ]));
};

export const {
    useGetStationQuery,
    useGetStationProductDefaultsQuery,
    useUpdateStationProductDefaultsMutation,
    useGetHousesQuery,
    useConsolidateRoomMutation,
    useUpdateStationMutation,
    useUpdateStationLocationMutation,
    useUploadStationLocationImageMutation,
    useRemoveStationLocationImageMutation,
    useDeleteHouseMutation,
    useCreateStationMutation,
    useCreateStationObjectsMutation,
    useLinkObjectsToProductMutation,
    useRelinkObjectMutation,
    useUpdateObjectMutation,
    useRetireObjectMutation,
    useReactivateObjectMutation,
    useGetRetiredObjectsQuery,
    useGetObjectLimitQuery,
    useDivideRoomMutation,
    useCombineRoomsMutation,
} = inventoryApiSlice;
